import { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    CircularProgress,
    Avatar,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { UserAvatar } from '../../../../app/components/userAvatar/UserAvatar';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../app/store';
import { getSearchResults } from '../../../../app/features/sessions/sessionSearch/sliceSearch';
import { User, UserMusicalGenre } from '../../../../app/#interfaces/interfaces';
import { useNavigate } from 'react-router-dom';
import * as REQUEST_REQUIREMENTS from '../../../../app/api/requestRequirements';

const BG_SECTION     = '#1c1c24';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

interface UserSearch {
    searchType: string;
    query: string | null;
}

export default function UsersSearch({ searchType, query }: UserSearch) {
    const dispatch = useDispatch<AppDispatch>();
    const [usersSearchPage, setUsersSearchPage] = useState(1);
    const authHeaders = useSelector((state: RootState) => state.session.authHeaders);
    const users = useSelector((state: RootState) => state.sessionSearch.users);
    const loadingMoreResults = useSelector((state: RootState) => state.sessionSearch.loadingMoreResults);
    const [isFetching, setIsFetching] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        async function fetchUsersData() {
            if (authHeaders && searchType && query && !isFetching) {
                await dispatch(
                    getSearchResults({
                        headers: authHeaders,
                        routeParams: {
                            queryType: 'users',
                            query: query,
                            page: usersSearchPage,
                        },
                    })
                );
                setIsFetching(false);
            }
        }
        fetchUsersData();
    }, []);

    useEffect(() => {
        function handleScroll() {
            if (
                window.innerHeight + document.documentElement.scrollTop !==
                    document.documentElement.offsetHeight ||
                isFetching
            )
                return;
            setUsersSearchPage((prevPage) => prevPage + 1);
        }

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isFetching]);

    async function handleNavigationUserProfile(userTarget: User) {
        if (userTarget) {
            const routeParams = {
                userNickname: userTarget.userMoreInfo?.nickname ?? (userTarget as any).nickname,
            };

            const PRIVATE_ROUTES = await REQUEST_REQUIREMENTS.handlePrivateRoutes({
                ROUTE_PARAMS: routeParams,
            });

            return navigate(PRIVATE_ROUTES.USER);
        }
    }

    return (
        <Box>
            {users.length > 0 &&
                users.map((user: User, index: number) => {
                    const selectedInstruments = user.userInstruments?.filter(
                        (ui: any) => ui.selected === true
                    ) ?? [];
                    const visibleInstruments = selectedInstruments.slice(0, 4);
                    const extraInstruments = selectedInstruments.length - visibleInstruments.length;

                    const selectedGenres =
                        user.userMusicalGenres?.filter(
                            (ug: UserMusicalGenre) => ug.selected === true
                        ) ?? [];
                    const visibleGenres = selectedGenres.slice(0, 4);
                    const extraGenres = selectedGenres.length - visibleGenres.length;

                    return (
                        <Box
                            key={index}
                            sx={{
                                bgcolor: BG_SECTION,
                                border: BORDER_SUBTLE,
                                borderRadius: '10px',
                                mb: 1.5,
                                p: 2,
                            }}
                        >
                            {/* Topo: avatar + nickname */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <UserAvatar src={user.userMoreInfo?.avatarUrl} size={44} />
                                <Typography
                                    onClick={() => handleNavigationUserProfile(user)}
                                    sx={{
                                        ml: 1.5,
                                        color: TEXT_PRIMARY,
                                        fontWeight: 700,
                                        fontSize: 15,
                                        cursor: 'pointer',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}
                                >
                                    {user.userMoreInfo?.nickname ?? (user as any).nickname}
                                </Typography>
                            </Box>

                            {/* About */}
                            {user.userMoreInfo?.about && (
                                <Typography
                                    sx={{
                                        color: TEXT_SECONDARY,
                                        fontSize: 13,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        mb: 1,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 4,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {user.userMoreInfo.about}
                                </Typography>
                            )}

                            {/* Localização */}
                            {user.userMoreInfo?.city &&
                                user.userMoreInfo?.state &&
                                user.userMoreInfo?.country && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                        <Chip
                                            icon={
                                                <LocationOnIcon
                                                    sx={{ color: `${TEXT_SECONDARY} !important`, fontSize: 14 }}
                                                />
                                            }
                                            label={`${user.userMoreInfo.city}, ${user.userMoreInfo.state} - ${user.userMoreInfo.country}`}
                                            size="small"
                                            sx={{
                                                bgcolor: 'rgba(255,255,255,0.06)',
                                                color: TEXT_SECONDARY,
                                                fontSize: 11,
                                                height: 24,
                                            }}
                                        />
                                    </Box>
                                )}

                            {/* Instrumentos */}
                            {selectedInstruments.length > 0 && (
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        sx={{ color: TEXT_SECONDARY, fontSize: 11, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.08em' }}
                                    >
                                        Instruments
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {visibleInstruments.map((ui: any, i: number) => (
                                            <Chip
                                                key={i}
                                                label={ui.instrument.name}
                                                size="small"
                                                sx={{
                                                    bgcolor: BRAND_RED,
                                                    color: TEXT_PRIMARY,
                                                    fontSize: 11,
                                                    height: 22,
                                                    fontWeight: 600,
                                                }}
                                            />
                                        ))}
                                        {extraInstruments > 0 && (
                                            <Chip
                                                label={`+${extraInstruments}`}
                                                size="small"
                                                sx={{
                                                    bgcolor: BRAND_RED,
                                                    color: TEXT_PRIMARY,
                                                    fontSize: 11,
                                                    height: 22,
                                                    fontWeight: 600,
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {/* Géneros musicais */}
                            {selectedGenres.length > 0 && (
                                <Box>
                                    <Typography
                                        sx={{ color: TEXT_SECONDARY, fontSize: 11, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.08em' }}
                                    >
                                        Musical Genres
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {visibleGenres.map((ug: UserMusicalGenre, i: number) => (
                                            <Chip
                                                key={i}
                                                label={ug.musicGenre.description}
                                                size="small"
                                                sx={{
                                                    bgcolor: 'rgba(255,255,255,0.08)',
                                                    color: TEXT_PRIMARY,
                                                    fontSize: 11,
                                                    height: 22,
                                                }}
                                            />
                                        ))}
                                        {extraGenres > 0 && (
                                            <Chip
                                                label={`+${extraGenres}`}
                                                size="small"
                                                sx={{
                                                    bgcolor: 'rgba(255,255,255,0.08)',
                                                    color: TEXT_PRIMARY,
                                                    fontSize: 11,
                                                    height: 22,
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    );
                })}

            {/* Estado vazio */}
            {users.length === 0 && !loadingMoreResults && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        mt: 8,
                        gap: 2,
                    }}
                >
                    <PersonSearchIcon sx={{ fontSize: 56, color: TEXT_SECONDARY }} />
                    <Typography sx={{ color: TEXT_SECONDARY, fontSize: 16 }}>
                        Nothing to show
                    </Typography>
                </Box>
            )}

            {/* Loading */}
            {loadingMoreResults && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress sx={{ color: BRAND_RED }} />
                </Box>
            )}
        </Box>
    );
}
